import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  History, Monitor, Smartphone, Trash2, X, GitCompare, Check,
} from 'lucide-react'
import toast from 'react-hot-toast'
import PageTransition from '@/components/PageTransition'
import GlassCard from '@/components/GlassCard'
import ScoreBadge from '@/components/ScoreBadge'
import RunLocationBadge from '@/components/RunLocationBadge'
import EmptyIllustration from '@/components/EmptyIllustration'
import { auditsApi } from '@/api/client'

export default function ReportsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [confirmingClear, setConfirmingClear] = useState(false)
  const [compareMode, setCompareMode] = useState(false)
  const [selected, setSelected] = useState<string[]>([])

  const { data, isLoading, isError } = useQuery({
    queryKey: ['reports'],
    queryFn: () => auditsApi.listReports(),
  })

  const clearMutation = useMutation({
    mutationFn: auditsApi.deleteAllReports,
    onSuccess: ({ deleted }) => {
      setConfirmingClear(false)
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      toast.success(
        deleted === 0
          ? 'Audit trail is already empty.'
          : `Cleared ${deleted} report${deleted === 1 ? '' : 's'}.`,
      )
    },
    onError: () => {
      toast.error('Failed to clear reports.')
    },
  })

  const reports = data?.content ?? []
  const hasReports = reports.length > 0
  const canCompare = hasReports && reports.length >= 2

  const enterCompareMode = () => {
    setCompareMode(true)
    setSelected([])
    setConfirmingClear(false)
  }

  const exitCompareMode = () => {
    setCompareMode(false)
    setSelected([])
  }

  const toggleSelected = (id: string) => {
    setSelected(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id)
      if (prev.length === 2) return [prev[1], id]   // bump oldest selection
      return [...prev, id]
    })
  }

  const handleRowClick = (id: string) => {
    if (compareMode) {
      toggleSelected(id)
    } else {
      navigate(`/reports/${id}`)
    }
  }

  const runCompare = () => {
    if (selected.length !== 2) return
    navigate(`/compare/${selected[0]}/${selected[1]}`)
  }

  return (
    <PageTransition>
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center gap-3 mb-8">
          <History size={20} className="text-accent" />
          <h1 className="text-xl font-bold">Audit History</h1>
          {data && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-[var(--text-secondary)]">
              {data.totalElements}
            </span>
          )}

          <div className="ml-auto flex items-center gap-2">
            {/* Compare-mode toolbar */}
            {compareMode && (
              <>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {selected.length === 0
                    ? 'Pick two reports'
                    : selected.length === 1
                      ? 'Pick one more'
                      : 'Ready to compare'}
                </span>
                <button
                  type="button"
                  onClick={runCompare}
                  disabled={selected.length !== 2}
                  className="text-xs font-medium px-3 py-1.5 rounded-md bg-accent/15 border border-accent/40 text-accent hover:bg-accent/25 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Compare
                </button>
                <button
                  type="button"
                  onClick={exitCompareMode}
                  aria-label="Cancel compare"
                  className="text-xs px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-white/20 transition-colors"
                >
                  <X size={12} />
                </button>
              </>
            )}

            {/* Clear-all confirmation */}
            {!compareMode && confirmingClear && (
              <>
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  Delete all {reports.length} reports?
                </span>
                <button
                  type="button"
                  onClick={() => clearMutation.mutate()}
                  disabled={clearMutation.isPending}
                  className="text-xs font-medium px-3 py-1.5 rounded-md bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25 transition-colors disabled:opacity-50"
                >
                  {clearMutation.isPending ? 'Clearing…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingClear(false)}
                  disabled={clearMutation.isPending}
                  aria-label="Cancel"
                  className="text-xs px-2 py-1.5 rounded-md bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-white/20 transition-colors disabled:opacity-50"
                >
                  <X size={12} />
                </button>
              </>
            )}

            {/* Default toolbar: Compare + Clear buttons */}
            {!compareMode && !confirmingClear && hasReports && (
              <>
                {canCompare && (
                  <button
                    type="button"
                    onClick={enterCompareMode}
                    className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-accent/40 hover:text-accent transition-colors"
                  >
                    <GitCompare size={12} />
                    Compare
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setConfirmingClear(true)}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-[var(--text-secondary)] hover:border-red-500/40 hover:text-red-300 transition-colors"
                >
                  <Trash2 size={12} />
                  Clear all
                </button>
              </>
            )}
          </div>
        </div>

        {isLoading && (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-16 rounded-xl bg-white/5 animate-pulse" />
            ))}
          </div>
        )}

        {isError && (
          <GlassCard className="text-center py-12 space-y-3">
            <EmptyIllustration
              variant="error"
              size={96}
              className="mx-auto text-[var(--text-secondary)]"
            />
            <p className="text-red-400 text-sm">Failed to load reports. Is the BFF running?</p>
          </GlassCard>
        )}

        {!isLoading && !isError && reports.length === 0 && (
          <GlassCard className="text-center py-14">
            <EmptyIllustration
              variant="empty"
              size={112}
              className="mx-auto mb-4 text-[var(--text-muted)]"
            />
            <p className="font-medium mb-1">No audits yet</p>
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
              Run your first audit from the home screen.
            </p>
          </GlassCard>
        )}

        <div className="space-y-2">
          {reports.map((report, i) => {
            const isSelected = selected.includes(report.id)
            return (
              <motion.div
                key={report.id}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.05, duration: 0.3 }}
              >
                <GlassCard
                  hover
                  onClick={() => handleRowClick(report.id)}
                  className={`flex items-center justify-between gap-4 py-3 transition-colors ${
                    compareMode && isSelected
                      ? 'ring-2 ring-accent/60 bg-accent/[0.06]'
                      : ''
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {compareMode && (
                      <span
                        className={`shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                          isSelected
                            ? 'bg-accent border-accent text-white'
                            : 'border-white/20'
                        }`}
                        aria-hidden="true"
                      >
                        {isSelected && <Check size={11} strokeWidth={3} />}
                      </span>
                    )}
                    {report.device === 'DESKTOP'
                      ? <Monitor size={16} className="shrink-0 text-[var(--text-muted)]" />
                      : <Smartphone size={16} className="shrink-0 text-[var(--text-muted)]" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{report.url}</p>
                      <p className="text-xs flex items-center gap-1.5" style={{ color: 'var(--text-muted)' }}>
                        {new Date(report.createdAt).toLocaleString()}
                        <RunLocationBadge runLocation={report.runLocation} />
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <ScoreBadge score={report.performanceScore} size="sm" />
                    <ScoreBadge score={report.accessibilityScore} size="sm" />
                    <ScoreBadge score={report.bestPracticesScore} size="sm" />
                    <ScoreBadge score={report.seoScore} size="sm" />
                  </div>
                </GlassCard>
              </motion.div>
            )
          })}
        </div>
      </div>
    </PageTransition>
  )
}
